import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { routes } from '../../constants';
import {
  Box,
  Button,
  Divider,
  Link,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import api from '../../api';
import StyledPage from '../../components/StyledPage';

export default function ForgotPasswordPage() {
  const defaultForm = {
    email: '',
  };

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // <600px

  const [userData, setUserData] = useState(defaultForm);
  const [inputErrors, setInputErrors] = useState({});
  const [linkSent, setLinkSent] = useState(false);

  const modifyFormData = (property, event) => {
    const dataCopy = { ...userData };
    dataCopy[property] = event.target.value;
    setUserData(dataCopy);
  };

  const resetPassword = async (event) => {
    event.preventDefault();

    let errors = {};
    if (userData.email.trim() === '') {
      errors = { email: <FormattedMessage id="register.email_required" /> };
    }
    if (Object.keys(errors).length !== 0) {
      setInputErrors(errors);
      return;
    }

    const resetStatus = await api.forgotPassword(userData);
    if (Object.keys(resetStatus.data).includes('message')) {
      setLinkSent(true);
    } else {
      setInputErrors({
        email: '',
        error: resetStatus.data.error || <FormattedMessage id="forgot_password.error" />,
      });
    }
  };

  return (
    <StyledPage
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: isMobile ? 'center' : 'space-between',
        alignItems: 'center',
        background: isMobile
          ? '#f5f5f5'
          : `url("https://images.unsplash.com/photo-1507521628349-dee6b8290f6f")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >

      {/* Form Section */}
      <Box
        sx={{
          flex: isMobile ? 'auto' : 1,
          p: { xs: 2, md: 5 },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: isMobile ? 'transparent' : 'rgba(255, 255, 255, 0.9)',
          backdropFilter: isMobile ? 'none' : 'blur(5px)',
          borderRadius: 1,
          maxWidth: { xs: '100%', md: '500px' },
          mx: 'auto',
          my: isMobile ? 0 : { xs: 1, md: 0 },
          boxShadow: isMobile ? '0px 2px 4px rgba(0, 0, 0, 0.1)' : 0,
          transform: isMobile ? 'translateY(-10%)' : 'none',
        }}
      >
        {isMobile && (
          <Typography
            variant="h5"
            sx={{
              mb: 3,
              color: '#00438E',
              textAlign: 'center',
              fontWeight: 'bold',
            }}
          >
            PromptaAI
          </Typography>
        )}
        <Typography
          variant="h6"
          sx={{
            mb: 3,
            color: '#00438E',
            textAlign: 'center',
            fontWeight: 'bold',
          }}
        >
          <FormattedMessage id="login.reset_password" />
        </Typography>
        {linkSent ? (
          <Typography
            sx={{
              color: '#00438E',
              textAlign: 'center',
              fontSize: 16,
            }}
          >
            <FormattedMessage id="login.link_sent" />
            <Box sx={{ mt: 2 }}>
              <Typography fontSize={14} sx={{ color: '#666' }}>
                <FormattedMessage id="login.back_to_login" />{' '}
                <Link
                  onClick={() => navigate(routes.login)}
                  sx={{
                    color: '#00438E',
                    '&:hover': { color: '#4A87C8' },
                    cursor: 'pointer',
                  }}
                >
                  <FormattedMessage id="login.sign_in" />
                </Link>
              </Typography>
            </Box>
          </Typography>
        ) : (
          <Box component="form" onSubmit={resetPassword} sx={{ width: '100%' }}>
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                error={
                  Object.keys(inputErrors).includes('email') ||
                  Object.keys(inputErrors).includes('error')
                }
                helperText={inputErrors['email'] || inputErrors['error'] || ''}
                slotProps={{ inputLabel: { shrink: true } }}
                size="small"
                type="email"
                label={<FormattedMessage id="register.email" />}
                value={userData.email}
                onChange={(event) => modifyFormData('email', event)}
                sx={{
                  '& .MuiInputBase-root': { backgroundColor: '#f0f0f0', borderRadius: 1 },
                  '& .MuiInputLabel-root': { color: '#00438E' },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#00438E' },
                    '&:hover fieldset': { borderColor: '#4A87C8' },
                    '&.Mui-focused fieldset': { borderColor: '#4A87C8' },
                  },
                }}
              />
            </Box>
            <Box sx={{ color: 'crimson', mb: 2, textAlign: 'center' }}>
              {Object.keys(inputErrors).includes('error') ? inputErrors['error'] : ''}
            </Box>
            <Divider sx={{ my: 2 }} />
            <Button
              size="large"
              type="submit"
              variant="contained"
              sx={{
                width: '100%',
                backgroundColor: '#00438E',
                '&:hover': { backgroundColor: '#003A75' },
                py: 1.5,
              }}
            >
              <FormattedMessage id="login.reset" />
            </Button>
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography fontSize={14} sx={{ color: '#666' }}>
                <FormattedMessage id="login.back_to_login" />{' '}
                <Link
                  onClick={() => navigate(routes.login)}
                  sx={{
                    color: '#00438E',
                    '&:hover': { color: '#4A87C8' },
                    cursor: 'pointer',
                  }}
                >
                  <FormattedMessage id="login.sign_in" />
                </Link>
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </StyledPage>
  );
}